import { useMemo, useState } from 'react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { StatusBadge } from '@/components/po/StatusBadge'
import { useAllPOs } from '@/hooks/usePOs'
import { useAdminDeletePO, useAdminDeletePOs } from '@/hooks/usePOMutations'

function fmtMoney(n: number): string {
  return n.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  })
}

export function DeletePOSection() {
  const [q, setQ] = useState('')
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const { data: pos = [], isLoading } = useAllPOs()
  const deleteMut = useAdminDeletePO()
  const bulkDeleteMut = useAdminDeletePOs()

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase()
    if (!needle) return pos
    return pos.filter((p) =>
      [p.po_number, p.vendor, p.po_id, p.status]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(needle))
    )
  }, [pos, q])

  const filteredIds = useMemo(() => filtered.map((p) => p.po_id), [filtered])
  const allSelected = filteredIds.length > 0 && filteredIds.every((id) => selected.has(id))
  const someSelected = filteredIds.some((id) => selected.has(id))
  const busy = deleteMut.isPending || bulkDeleteMut.isPending

  function toggleOne(id: string) {
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  function toggleAll() {
    setSelected((prev) => {
      const next = new Set(prev)
      if (allSelected) {
        for (const id of filteredIds) next.delete(id)
      } else {
        for (const id of filteredIds) next.add(id)
      }
      return next
    })
  }

  function confirmDelete(poId: string, label: string, status: string) {
    toast(`Permanently delete PO ${label} (${status})?`, {
      description: 'This cannot be undone. Line items and scan history are removed too.',
      action: {
        label: 'Delete',
        onClick: () =>
          deleteMut.mutate(poId, {
            onSuccess: () => {
              toast.success(`Deleted PO ${label}`)
              setSelected((prev) => {
                const next = new Set(prev)
                next.delete(poId)
                return next
              })
            },
            onError: (err) =>
              toast.error(err instanceof Error ? err.message : 'Failed to delete PO'),
          }),
      },
    })
  }

  function confirmBulkDelete() {
    const ids = filteredIds.filter((id) => selected.has(id))
    if (ids.length === 0) return
    toast(`Permanently delete ${ids.length} purchase order(s)?`, {
      description: 'This cannot be undone. Line items and scan history are removed too.',
      action: {
        label: `Delete ${ids.length}`,
        onClick: () =>
          bulkDeleteMut.mutate(ids, {
            onSuccess: (res) => {
              if (res.failures.length === 0) {
                toast.success(`Deleted ${res.deleted} purchase order(s)`)
              } else {
                toast.error(
                  `Deleted ${res.deleted}; ${res.failures.length} failed: ` +
                    res.failures.map((f) => `${f.po_id} (${f.error})`).join(', ')
                )
              }
              setSelected((prev) => {
                const next = new Set(prev)
                for (const id of ids) {
                  if (!res.failures.some((f) => f.po_id === id)) next.delete(id)
                }
                return next
              })
            },
            onError: (err) =>
              toast.error(err instanceof Error ? err.message : 'Bulk delete failed'),
          }),
      },
    })
  }

  const selectedCount = filteredIds.filter((id) => selected.has(id)).length

  return (
    <section className="space-y-3">
      <h2 className="text-sm font-semibold">Delete Purchase Order</h2>
      <p className="text-xs text-[var(--mute)]">
        Permanently delete a PO in any status — including ordered or received ones.
        This is a manual cleanup tool: deletion removes the PO, its line items, and
        scan history, and cannot be undone. It does not adjust Shopify inventory.
      </p>

      <div className="flex items-center gap-2">
        <input
          type="text"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Filter by PO #, vendor, or status…"
          className="flex-1 px-2 py-1 border border-[var(--line-input)] bg-[var(--surface-card)] text-xs focus:outline-none focus:border-[var(--brand)]"
          data-testid="delete-po-search"
        />
        <Button
          variant="ghost"
          size="sm"
          className="h-7 text-xs text-destructive hover:text-destructive disabled:opacity-40"
          disabled={selectedCount === 0 || busy}
          onClick={confirmBulkDelete}
        >
          {bulkDeleteMut.isPending ? 'Deleting…' : `Delete selected (${selectedCount})`}
        </Button>
      </div>

      <div className="overflow-auto border border-[var(--line-warm)] bg-[var(--surface-card)] max-h-[480px]">
        <table className="text-xs w-full">
          <thead className="bg-[var(--surface-header)] border-b border-[var(--line-warm)] sticky top-0">
            <tr className="text-left text-[var(--mute)]">
              <th className="tbl-th w-8">
                <input
                  type="checkbox"
                  checked={allSelected}
                  ref={(el) => {
                    if (el) el.indeterminate = someSelected && !allSelected
                  }}
                  onChange={toggleAll}
                  disabled={filteredIds.length === 0}
                  className="h-3.5 w-3.5 cursor-pointer"
                  aria-label="Select all"
                />
              </th>
              <th className="tbl-th w-28">PO #</th>
              <th className="tbl-th">Vendor</th>
              <th className="tbl-th w-36">Status</th>
              <th className="tbl-th w-16 text-right">Items</th>
              <th className="tbl-th w-24 text-right">Total</th>
              <th className="tbl-th w-20" />
            </tr>
          </thead>
          <tbody>
            {isLoading && (
              <tr>
                <td colSpan={7} className="px-2 py-4 text-center text-[var(--mute)]">
                  Loading…
                </td>
              </tr>
            )}
            {!isLoading && filtered.length === 0 && (
              <tr>
                <td colSpan={7} className="px-2 py-4 text-center text-[var(--mute)]">
                  No purchase orders.
                </td>
              </tr>
            )}
            {filtered.map((p, i) => {
              const label = p.po_number || p.po_id
              const isChecked = selected.has(p.po_id)
              return (
                <tr
                  key={p.po_id}
                  className={`border-b border-[var(--line-warm)] ${
                    isChecked
                      ? 'bg-[var(--surface-header)]'
                      : i % 2 === 1
                        ? 'bg-[var(--surface-zebra)]'
                        : 'bg-[var(--surface-card)]'
                  }`}
                >
                  <td className="px-2 py-1">
                    <input
                      type="checkbox"
                      checked={isChecked}
                      onChange={() => toggleOne(p.po_id)}
                      className="h-3.5 w-3.5 cursor-pointer"
                      aria-label={`Select PO ${label}`}
                    />
                  </td>
                  <td className="px-2 py-1 font-medium">{label}</td>
                  <td className="px-2 py-1 text-[var(--ink-2)]">{p.vendor || '—'}</td>
                  <td className="px-2 py-1">
                    <StatusBadge status={p.status} />
                  </td>
                  <td className="px-2 py-1 text-right tabular-nums">{p.items_count}</td>
                  <td className="px-2 py-1 text-right tabular-nums">
                    {fmtMoney(p.total_cost_base)}
                  </td>
                  <td className="px-2 py-1 text-right">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-6 text-[11px] text-destructive hover:text-destructive"
                      disabled={busy}
                      onClick={() => confirmDelete(p.po_id, label, p.status)}
                    >
                      Delete
                    </Button>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </section>
  )
}
